import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { AppState } from '../../store/app.state';
import { WebSocketActions } from '../../store/actions/websocket.actions';
import { selectIsConnected } from '../../store/selectors/websocket.selectors';
import { selectIsAuthenticated, selectToken } from '../../store/selectors/auth.selectors';

/**
 * Bootstrap service to initialize WebSocket connections and real-time features
 * This service should be provided in root and initialized early in the app lifecycle
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketBootstrapService implements OnDestroy {
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor(private store: Store<AppState>) {}

  /**
   * Initialize WebSocket connections and real-time features
   * Should be called after user authentication
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || !environment.features.enableWebSocket) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeInternal().finally(() => {
      this.initializationPromise = null;
    });

    return this.initializationPromise;
  }

  private async initializeInternal(): Promise<void> {
    console.log('Initializing WebSocket Bootstrap Service...');

    try {
      await this.waitForAuthentication();

      const token = await this.getToken();
      this.store.dispatch(WebSocketActions.connect({ token }));
      this.store.dispatch(WebSocketActions.subscribeToDashboard());

      this.isInitialized = true;
      await this.waitForConnection();

      console.log('WebSocket Bootstrap Service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize WebSocket Bootstrap Service:', error);
    }
  }

  /**
   * Cleanup and disconnect
   */
  shutdown(): void {
    if (!this.isInitialized && !this.initializationPromise) {
      return;
    }

    console.log('Shutting down WebSocket Bootstrap Service...');
    
    this.store.dispatch(WebSocketActions.unsubscribeFromDashboard());
    this.store.dispatch(WebSocketActions.disconnect());
    
    this.isInitialized = false;
    this.initializationPromise = null;
  }

  /**
   * Get initialization status
   */
  isWebSocketInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Reconnect if needed
   */
  reconnectIfNeeded(): void {
    if (!this.isInitialized) {
      return;
    }

    this.store.select(selectIsConnected)
      .pipe(take(1))
      .subscribe(isConnected => {
        if (!isConnected) {
          console.log('Reconnecting WebSocket...');
          this.store.select(selectToken).pipe(take(1)).subscribe(token => {
            this.store.dispatch(WebSocketActions.connect({ token: token || undefined }));
          });
        }
      });
  }

  private getToken(): Promise<string | undefined> {
    return new Promise((resolve) => {
      this.store.select(selectToken)
        .pipe(take(1))
        .subscribe(token => resolve(token || undefined));
    });
  }

  private waitForAuthentication(): Promise<void> {
    return new Promise((resolve) => {
      this.store.select(selectIsAuthenticated)
        .pipe(
          filter(isAuthenticated => isAuthenticated),
          take(1)
        )
        .subscribe(() => {
          resolve();
        });
    });
  }

  private waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, environment.websocket.timeout);

      this.store.select(selectIsConnected)
        .pipe(
          filter(isConnected => isConnected),
          take(1)
        )
        .subscribe(() => {
          clearTimeout(timeout);
          resolve();
        });
    });
  }

  ngOnDestroy(): void {
    this.shutdown();
  }
}
